/**
 * Locale detection utilities for AxiomGO.
 *
 * Pure functions — no DOM or window access. All browser globals are
 * injected via parameters so this module is fully testable in Vitest/Node.
 *
 * Supported locales today: pt-BR, en-US, es-419.
 *
 * Fallback chain:
 *   pt-* (pt-BR, pt-PT, …)         → pt-BR
 *   en-* (en-US, en-GB, …)         → en-US
 *   es-* (es-AR, es-MX, es-CL, …)  → es-419  (Spanish Latam — never Spain peninsular)
 *   any other (ar-*, fr-*, de-*, …) → en-US  (lingua franca industrial)
 *
 * FUTURE: ar-SA support tracked in
 *   axiomgo-brain/backlog/2026-04-30-arabic-locale-rtl.md
 */

/** Subset of the Navigator interface used for detection — injectable for tests. */
export interface NavigatorLocale {
  readonly languages?: readonly string[]
  readonly language?: string
}

/**
 * Map a single BCP-47 language tag to one of the supported locales.
 * Returns `null` when no match is found so the caller can try the next candidate.
 */
function mapTag(tag: string, supported: readonly string[]): string | null {
  const lower = tag.toLowerCase()

  if (lower.startsWith('pt')) {
    const match = supported.find((s) => s.toLowerCase().startsWith('pt'))
    return match ?? null
  }

  if (lower.startsWith('en')) {
    const match = supported.find((s) => s.toLowerCase().startsWith('en'))
    return match ?? null
  }

  if (lower.startsWith('es')) {
    // Map any Spanish variant (es-AR, es-MX, es-CL, es-ES, …) to the
    // Latam catalog. AxiomGO does NOT ship a peninsular Spain locale —
    // industrial vocab is Latam (OT, equipo, planta) by design.
    const match = supported.find((s) => s.toLowerCase().startsWith('es'))
    return match ?? null
  }

  // Exact match for any other supported locale (future-proof).
  const exact = supported.find((s) => s.toLowerCase() === lower)
  if (exact) return exact

  // FUTURE: ar-SA support tracked in
  //   axiomgo-brain/backlog/2026-04-30-arabic-locale-rtl.md
  // For now all non-pt / non-en / non-es tags (ar-*, fr-*, de-*, zh-*, …)
  // fall through to the default below.
  return null
}

/**
 * Detect the best supported locale from the browser's language preferences.
 *
 * @param nav     - Injectable navigator-like object (`navigator.languages` / `navigator.language`).
 * @param supported - Ordered list of supported locale codes (e.g. `['pt-BR', 'en-US']`).
 * @returns The best matching supported locale, or the first entry in `supported`
 *          as ultimate fallback. Throws only if `supported` is empty.
 *
 * #426 (Brazilian-first): Portuguese SHORT-CIRCUITS preference order — if pt
 * appears ANYWHERE in the candidates, pt-BR wins, even over an earlier non-pt
 * entry (e.g. en or es listed first). Only a user with NO Portuguese at all
 * follows the ordered en/es detection below. This intentionally violates strict
 * first-match-wins for pt and only for pt.
 *
 * @example
 * detectLocale({ languages: ['ar-SA', 'en-US'] }, ['pt-BR', 'en-US']) // → 'en-US'
 * detectLocale({ languages: ['en-US', 'pt-BR'] }, ['pt-BR', 'en-US']) // → 'pt-BR' (pt short-circuit)
 * detectLocale({ languages: ['es-MX', 'pt-PT'] }, SUPPORTED)          // → 'pt-BR' (pt beats es)
 * detectLocale({ language: 'pt-PT' }, ['pt-BR', 'en-US'])             // → 'pt-BR'
 */
export function detectLocale(nav: NavigatorLocale, supported: string[]): string {
  if (supported.length === 0) {
    throw new Error('detectLocale: supported list must not be empty')
  }

  // Build candidate list: navigator.languages (ordered preference) takes priority.
  const candidates: string[] = []

  if (nav.languages && nav.languages.length > 0) {
    candidates.push(...nav.languages)
  } else if (nav.language) {
    candidates.push(nav.language)
  }

  // #426 (Denis, Brazilian-first): if Portuguese appears ANYWHERE in the
  // preferences, prefer pt-BR — a Brazilian whose device lists en-US first still
  // wants Portuguese. "Não português vê inglês" = only a user with NO Portuguese
  // at all falls through to the ordered en/es detection below.
  for (const candidate of candidates) {
    if (candidate.toLowerCase().startsWith('pt')) {
      const pt = mapTag(candidate, supported)
      if (pt !== null) return pt
    }
  }

  for (const candidate of candidates) {
    const resolved = mapTag(candidate, supported)
    if (resolved !== null) return resolved
  }

  // Ultimate fallback — en-US as industrial lingua franca.
  // If en-US is not in the supported list, fall back to the first entry.
  return supported.find((s) => s.toLowerCase().startsWith('en')) ?? supported[0]
}

/**
 * RAIZ 10 (2026-07-25) — chaves SEPARADAS para escolha e palpite.
 *
 * Antes, `resolveInitialLocale` gravava o valor AUTO-DETECTADO na mesma chave que
 * lia como "override do usuário". Depois do primeiro load, escolha manual e
 * palpite do navegador ficavam indistinguíveis, e qualquer guard que dependesse
 * dessa distinção nunca disparava. Era isso que tornava o M12 irreparável por
 * configuração.
 */
/** Escolha EXPLÍCITA do usuário. Só é escrita quando ele troca o idioma. */
export const USER_LOCALE_KEY = 'axg-locale-user'
/** Palpite do navegador. Diagnóstico/telemetria, NUNCA fonte de decisão. */
export const AUTODETECT_LOCALE_KEY = 'axg-locale-autodetect'

/**
 * Idioma default do produto.
 *
 * Decisão do Denis (2026-07-25): PT-BR, porque o produto é vendido em português e
 * o motor é monolíngue PT-BR. O default anterior caía em en-US para qualquer
 * browser sem `pt`, e o resultado era o balão do motor em português colado num
 * card do front em inglês, na mesma tela (M12).
 */
export const DEFAULT_PRODUCT_LOCALE = 'pt-BR'

/**
 * Resolve o idioma inicial. O PRODUTO decide, nunca o navegador.
 *
 * Precedência:
 *  1. escolha EXPLÍCITA do usuário (`USER_LOCALE_KEY`), se suportada;
 *  2. preferência do TENANT (o motor publica em `harness.i18n.defaultLanguage`),
 *     passada por `tenantLocale`;
 *  3. {@link DEFAULT_PRODUCT_LOCALE}.
 *
 * O navegador NÃO entra na precedência. O palpite dele é gravado em
 * `AUTODETECT_LOCALE_KEY` apenas para diagnóstico, e nunca é lido como decisão.
 *
 * @param storage - Storage injetável (default `localStorage`).
 * @param nav - Navigator injetável (default `navigator`), só para o palpite.
 * @param supported - Idiomas suportados.
 * @param tenantLocale - Preferência do tenant, quando conhecida.
 */
export function resolveInitialLocale(
  storage: Pick<Storage, 'getItem' | 'setItem'> = localStorage,
  nav: NavigatorLocale = navigator,
  supported: string[] = ['pt-BR', 'en-US', 'es-419'],
  tenantLocale?: string,
): string {
  // Registra o palpite do navegador em chave PRÓPRIA. Nunca decide nada.
  try {
    storage.setItem(AUTODETECT_LOCALE_KEY, detectLocale(nav, supported))
  } catch {
    // storage indisponível (modo privado estrito) não pode derrubar o boot.
  }

  const userChoice = storage.getItem(USER_LOCALE_KEY)
  if (userChoice !== null && supported.includes(userChoice)) return userChoice

  if (tenantLocale !== undefined && supported.includes(tenantLocale)) return tenantLocale

  return supported.includes(DEFAULT_PRODUCT_LOCALE) ? DEFAULT_PRODUCT_LOCALE : supported[0]
}
